// Structural match for ClipQualityOption, kept local so this stays a pure
// utility rather than depending on the hook that fetches them.
export interface IQualityOption {
  configName: string;
  settings: { resolution: string; framerate: number; frameBlendBlur: number };
  requiredUserGroup: string;
  canApply: boolean;
}

export type TQualityMatch =
  | { status: "exact" | "closest"; config: IQualityOption }
  | { status: "tier"; requiredUserGroup: string; resolution: string }
  | { status: "none" };

const ratioOf = (resolution: string): number | null => {
  const match = resolution.match(/(\d+)x(\d+)/);
  return match ? Number(match[1]) / Number(match[2]) : null;
};

const heightOf = (resolution: string): number => {
  const match = resolution.match(/(\d+)x(\d+)/);
  return match ? Number(match[2]) : 0;
};

// Highest framerate wins, then least motion blur: blur is a deliberate look, so
// it should not arrive by accident just because a config happened to sort first.
const best = (options: IQualityOption[]): IQualityOption =>
  [...options].sort(
    (a, b) =>
      b.settings.framerate - a.settings.framerate ||
      a.settings.frameBlendBlur - b.settings.frameBlendBlur,
  )[0];

/**
 * Picks the clip quality config that best matches the resolution the player
 * actually runs the game at. Never changes their aspect ratio: a 4:3 player
 * getting a 16:9 clip is worse than us leaving the setting alone.
 */
export const matchQualityConfig = (
  resolution: { width: number; height: number },
  configs: IQualityOption[],
): TQualityMatch => {
  const target = `${resolution.width}x${resolution.height}`;
  const exact = configs.filter((c) => c.settings.resolution === target);
  const applicableExact = exact.filter((c) => c.canApply);

  if (applicableExact.length) {
    return { status: "exact", config: best(applicableExact) };
  }

  // Their resolution exists but their tier cannot have it. Say so rather than
  // quietly downgrading them to something they did not choose.
  if (exact.length) {
    return {
      status: "tier",
      requiredUserGroup: exact[0].requiredUserGroup,
      resolution: target,
    };
  }

  const targetRatio = resolution.width / resolution.height;
  const sameAspect = configs.filter((c) => {
    const ratio = ratioOf(c.settings.resolution);
    return c.canApply && ratio !== null && Math.abs(ratio - targetRatio) < 0.01;
  });

  if (!sameAspect.length) return { status: "none" };

  // Closest without going over, so we never upscale past their own display.
  // When everything we carry is larger than their display, take the smallest
  // rather than the largest: the point is to stay near their resolution.
  const notAbove = sameAspect.filter(
    (c) => heightOf(c.settings.resolution) <= resolution.height,
  );
  const heights = (notAbove.length ? notAbove : sameAspect).map((c) =>
    heightOf(c.settings.resolution),
  );
  const targetHeight = notAbove.length
    ? Math.max(...heights)
    : Math.min(...heights);
  const pool = notAbove.length ? notAbove : sameAspect;

  return {
    status: "closest",
    config: best(
      pool.filter((c) => heightOf(c.settings.resolution) === targetHeight),
    ),
  };
};
